import type { PageResult } from '@jiaofa/api-types'
import type {
  DeliveryType,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  RefundStatus,
} from '@jiaofa/constants'
import { request } from './request'

export interface AdminOrderItem {
  id: string
  productId: string
  productName: string
  specificationText?: string
  quantity: number
  unitPrice: number
  totalAmount: number
  image?: string
}

export interface AdminOrder {
  id: string
  orderNo: string
  userId: string
  userNickname?: string
  userPhone?: string
  storeId: string
  storeName?: string
  status: OrderStatus
  paymentStatus: PaymentStatus
  paymentMethod?: PaymentMethod
  /** 是否实际退款：存在退款流水或状态为已退款；取消未退款为 false */
  refunded?: boolean
  deliveryType: DeliveryType
  items: AdminOrderItem[]
  goodsAmount: number
  discountAmount: number
  deliveryFee: number
  payableAmount: number
  paidAmount: number
  pickupNo?: string
  remark?: string
  createdAt: string
}

type AdminOrderResponse = Omit<AdminOrder, 'refunded'> & {
  refunded?: unknown
}

export interface OrderListParams {
  page?: number
  pageSize?: number
  orderNo?: string
  phone?: string
  storeId?: string
  status?: OrderStatus | ''
  deliveryType?: DeliveryType | ''
}

export interface UpdateOrderStatusPayload {
  status: OrderStatus
  remark?: string
}

export interface AdminRefund {
  id: string
  refundNo: string
  orderId: string
  orderNo?: string
  userId: string
  amount: number
  reason?: string
  status: RefundStatus
  channel?: string
  createdAt: string
}

export interface RefundListParams {
  page?: number
  pageSize?: number
}

export interface RefundAuditPayload {
  result: 'APPROVED' | 'REJECTED'
  reason?: string
}

export interface WechatRefundStatusResult {
  refundNo: string
  orderNo: string
  refundStatus: RefundStatus
  wechatStatus: string
  wechatRefundId?: string
  message?: string
  refundedAt?: string
}

export const orderApiReady = true as const

const adaptAdminOrder = (raw: AdminOrderResponse): AdminOrder => ({
  ...raw,
  refunded: Boolean(raw.refunded),
})

export const fetchOrders = async (params: OrderListParams): Promise<PageResult<AdminOrder>> => {
  const result = await request<PageResult<AdminOrderResponse>>({
    url: '/orders',
    method: 'GET',
    params,
  })
  return {
    ...result,
    list: result.list.map(adaptAdminOrder),
  }
}

export const fetchOrderDetail = async (id: string): Promise<AdminOrder> =>
  adaptAdminOrder(await request<AdminOrderResponse>({
    url: `/orders/${id}`,
    method: 'GET',
  }))

export const updateOrderStatus = async (
  id: string,
  payload: UpdateOrderStatusPayload,
): Promise<void> =>
  request<void, UpdateOrderStatusPayload>({
    url: `/orders/${id}/status`,
    method: 'PUT',
    data: payload,
  })

export const fetchRefunds = async (params: RefundListParams): Promise<PageResult<AdminRefund>> =>
  request<PageResult<AdminRefund>>({
    url: '/refunds',
    method: 'GET',
    params,
  })

export const auditRefund = async (id: string, payload: RefundAuditPayload): Promise<void> =>
  request<void, RefundAuditPayload>({
    url: `/refunds/${id}/audit`,
    method: 'POST',
    data: payload,
  })

export const refreshWechatRefundStatus = async (id: string): Promise<WechatRefundStatusResult> =>
  request<WechatRefundStatusResult>({
    url: `/refunds/${id}/wechat-status`,
    method: 'GET',
  })
